import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PosService } from '../../services/pos.service';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { PosDashboardSummary } from '../../interfaces/pos.interface';
import { formatMoney } from '../../utils/currency';
import { formatDateTime } from '../../utils/dateUtils';

// The same four roles PosReportController's /dashboard endpoint grants (@PreAuthorize on the
// backend is the real boundary; this is only what decides whether the client bothers to render
// the tiles at all, exactly the posture Dashboard.tsx's own role branches already take).
const DASHBOARD_ROLES: UserType[] = [
    UserType.InstitutionAdmin, UserType.StoreAdmin, UserType.StoreManager, UserType.Accountant,
];

// The narrower set PosSaleQueryController's /unposted endpoint grants -- NO StoreManager. A
// StoreManager can see this tile's unpostedCount (the dashboard endpoint admits that role), but
// sending them to /pos/unposted would 403: that screen's own @PreAuthorize excludes StoreManager.
// Decision (Task 11 brief item 4): the tile still shows the count for a StoreManager, just never
// as a link -- the number stays informative without steering the operator into a dead end.
const UNPOSTED_QUEUE_ROLES: UserType[] = [
    UserType.InstitutionAdmin, UserType.StoreAdmin, UserType.Accountant,
];

const Tile: React.FC<{ label: string; value: React.ReactNode; testId: string }> = ({ label, value, testId }) => (
    <Paper sx={{ p: 2, flex: '1 1 180px', minWidth: 160 }} data-testid={testId}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6">{value}</Typography>
    </Paper>
);

const PosDashboardTiles: React.FC = () => {
    const toast = useToast();
    const { user } = useAuth();

    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const hasAnyRole = (roles: UserType[]) => roles.some(r => userRoles.includes(r));

    const [summary, setSummary] = useState<PosDashboardSummary | null>(null);
    // Task 11 fix round 1 (F4): this used to be set but never observably rendered -- `if
    // (loadError) return null` was unreachable-equivalent, since `summary` stays null on the
    // error path and the very next check (`if (!summary) return null`) already returns null by
    // itself. Deleting the flag entirely passed the whole test suite. It now drives a real error
    // Alert with a Retry action, the same pattern Task 10's day-end report screen was required to
    // grow for the identical situation (a failed load must not just vanish behind a toast).
    const [loadError, setLoadError] = useState(false);

    const canSeeTiles = hasAnyRole(DASHBOARD_ROLES);
    const canOpenUnpostedQueue = hasAnyRole(UNPOSTED_QUEUE_ROLES);

    const load = () => {
        setLoadError(false);
        PosService.getDashboardSummary()
            .then(data => setSummary(data))
            .catch(err => {
                setLoadError(true);
                toast.showApiError(err, 'Failed to load the POS dashboard summary');
            });
    };

    useEffect(() => {
        if (!canSeeTiles) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSeeTiles]);

    if (!canSeeTiles) return null;

    if (loadError) {
        return (
            <Box sx={{ mb: 3 }} data-testid="dashboard-load-error">
                <Alert
                    severity="error"
                    action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
                >
                    Failed to load the POS dashboard summary.
                </Alert>
            </Box>
        );
    }

    if (!summary) return null;

    // The most common state this component renders in: a fresh morning with no sales yet.
    // Tiles still render (their zeros are real information), plus explicit copy so a blank-
    // looking "৳0.00" everywhere doesn't read as a broken/loading dashboard.
    const noSalesYetToday = summary.transactionCountToday === 0;

    const hasUnpostedSales = summary.unpostedCount > 0;

    const unpostedValue = hasUnpostedSales && canOpenUnpostedQueue
        ? (
            <RouterLink to="/pos/unposted" data-testid="dashboard-unposted-link">
                {summary.unpostedCount}
            </RouterLink>
        )
        : String(summary.unpostedCount);

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>POS Today</Typography>

            {noSalesYetToday && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="dashboard-no-sales-yet">
                    No sales yet today.
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Tile label="Gross Sales Today" value={formatMoney(summary.grossSalesToday)} testId="dashboard-tile-gross" />
                <Tile label="Transactions Today" value={String(summary.transactionCountToday)} testId="dashboard-tile-transactions" />
                <Tile label="Average Basket Today" value={formatMoney(summary.averageBasketToday)} testId="dashboard-tile-average-basket" />
                <Tile label="Unposted Sales" value={unpostedValue} testId="dashboard-tile-unposted" />
                {/* Task 11 fix round 1 (F5c): lastSaleAt was computed, serialised and typed but
                    rendered nowhere -- a dead field on the wire contract. Deliberately NOT
                    windowed to today (see the DTO's own javadoc), so a quiet terminal shows a
                    stale timestamp here rather than the tile disappearing once its last sale ages
                    out of "today". */}
                <Tile
                    label="Last Sale"
                    value={summary.lastSaleAt ? formatDateTime(summary.lastSaleAt) : 'No sales yet'}
                    testId="dashboard-tile-last-sale"
                />
            </Box>

            {/* A StoreManager can see the count above (the backend grants that role this
                endpoint), but the "Unposted POS sales" screen itself excludes StoreManager --
                sending them there would 403. This caption is the only affordance for that role
                when there IS a nonzero count to explain why it isn't a link. */}
            {hasUnpostedSales && !canOpenUnpostedQueue && (
                <Typography variant="caption" color="text.secondary" data-testid="dashboard-unposted-escalate-hint">
                    Ask a store admin or accountant to review the unposted queue.
                </Typography>
            )}
        </Box>
    );
};

export default PosDashboardTiles;
